// hooks
import { Fragment, useEffect, useState } from "react";
import useHttp from "hooks/use-http";

// routes
import { useNavigate, useParams } from "react-router-dom";

import { MdEdit, MdDelete } from "react-icons/md";

// material styles
import { Grid, IconButton, TextField, Tooltip } from "@mui/material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import DeleteModal from "components/shared/Modal/Delete";

// constants
import { PAGINATION_LIMIT } from "constants";

// helper
import Toast from "helper/toast";
import GenerateHeadCell from "helper/generate-head-cell";
import {
  deleteClassScoreByIdRequest,
  getTeacherClassScoresRequest,
} from "api/requests";
import { StyledDateBox, StyledStack } from "./DisplayScores.style";
import DatePickerCM from "components/shared/DatePicker";
import { StyledGridIcons } from "../DisplayClasses.style";
import { Delete, FilterAlt } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { convertNum } from "helper/convert-num";

// teacher head cells
const headCells = GenerateHeadCell("teacher_class_scores_list");

export default function TeacherClassScores() {
  const { class_id } = useParams();

  const navigate = useNavigate();

  const { request, loading, setLoading } = useHttp(false);

  // class scores data state
  const [classScoresData, setClassScoresData] = useState({
    data: [],
    count: 0,
  });

  // page state for pagination
  const [page, setPage] = useState(1);

  // delete class score id
  const [deleteId, setDeleteId] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [className, setClassName] = useState("");

  const { register } = useForm();

  // date filter handler
  const onFromDateHandler = (value) =>
    setFromDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const onToDateHandler = (value) =>
    setToDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // handle delete modal (show and hide) and set class id for delete score
  const handleShowDeleteModal = (rowId) => {
    setShowDeleteModal((state) => !state);
    setDeleteId(rowId);
  };

  // delete class score data by id
  const onDelete = async () => {
    const res = await request({
      url: deleteClassScoreByIdRequest,
      values: deleteId,
    });
    if (res) {
      getClassScoresList();
      setShowDeleteModal(false);
      Toast({ type: "success", title: "حذف نمره کلاس " });
    }
  };

  // get all class scores data
  const getClassScoresList = async (filter = true) => {
    setLoading(true);

    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      class_id: class_id,
      to_date: toDate ?? null,
      from_date: fromDate ?? null,
    };

    const res = await request({
      url: getTeacherClassScoresRequest,
      values: filter
        ? query
        : {
            skip: 0,
            limit: PAGINATION_LIMIT,
            class_id: class_id,
          },
    });

    setLoading(false);

    if (res) {
      if (res.count < 1) {
        return setClassScoresData({
          count: res.count,
          data: [{ content: "هیچ نمره ی کلاسی یافت نشد" }],
        });
      }

      let transformedData = res.data.map((items) => ({
        id: items.id,
        date: items.date.string,
        course_name: items.classModel.course.title,
        added_by:
          items.added_by_type === "admin"
            ? "اضافه شده توسط ادمین"
            : "اضافه شده توسط مدرس",
        edit: (
          <Tooltip title="ویرایش" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(
                  "/teacher_class/" + class_id + "/scores/edit/" + items.id
                );
              }}
            >
              <MdEdit />
            </IconButton>
          </Tooltip>
        ),
        delete: (
          <Tooltip title="حذف" placement="top">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleShowDeleteModal(items.id);
              }}
            >
              <MdDelete style={{ color: "#d32f2f", fontSize: "25px" }} />
            </IconButton>
          </Tooltip>
        ),
      }));

      setClassName(res.data[0].classModel?.title);

      setClassScoresData({ data: transformedData, count: res.count });
    }
  };

  const getWithFilter = () => {
    setPage(1);
    getClassScoresList();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setFromDate("");
    setToDate("");
    getClassScoresList(false);
  };

  useEffect(() => {
    getClassScoresList();
  }, [page]);

  return (
    <DrawerCM title={"لیست نمرات کلاس " + className ?? ""} $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item my={2} xs={12}>
              <BackButton Route={`/teacher_classes/`} />
            </Grid>
            <StyledStack>
              <Grid container>
                <Grid item sm={5} xs={12} py={1} px={1}>
                  <StyledDateBox>
                    <DatePickerCM
                      onChange={onFromDateHandler}
                      render={(value, openCalendar) => {
                        return (
                          <TextField
                            label="از تاریخ"
                            variant="outlined"
                            fullWidth
                            size="small"
                            id="from_created_at"
                            {...register("from_created_at")}
                            value={
                              fromDate ? fromDate.split("-").join("/") : ""
                            }
                            onClick={openCalendar}
                          />
                        );
                      }}
                    />
                  </StyledDateBox>
                </Grid>
                <Grid item sm={5} xs={12} p={1}>
                  <StyledDateBox>
                    <DatePickerCM
                      onChange={onToDateHandler}
                      render={(value, openCalendar) => {
                        return (
                          <TextField
                            label="تا تاریخ"
                            variant="outlined"
                            fullWidth
                            size="small"
                            id="to_created_at"
                            {...register("to_created_at")}
                            value={toDate ? toDate.split("-").join("/") : ""}
                            onClick={openCalendar}
                          />
                        );
                      }}
                    />
                  </StyledDateBox>
                </Grid>
                <StyledGridIcons item sm={2} xs={12} p={1}>
                  <IconButton color="primary" onClick={getWithFilter}>
                    <Tooltip title="اعمال فیلتر" placement="top">
                      <FilterAlt />
                    </Tooltip>
                  </IconButton>
                  <IconButton color="error" onClick={getWithoutFilter}>
                    <Tooltip title="حذف فیلتر" placement="top">
                      <Delete />
                    </Tooltip>
                  </IconButton>
                </StyledGridIcons>
              </Grid>
            </StyledStack>
            <Grid item xs={12} my={2}>
              <TableCM
                title={`لیست نمرات کلاس `}
                rows={classScoresData.data}
                headCells={headCells}
                addClassScore={class_id}
                Id={class_id}
                page={page}
                colspan={classScoresData.count === 0 ? "6" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              {classScoresData.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={classScoresData.count}
                />
              )}
            </Grid>
          </Grid>
          <DeleteModal
            show={showDeleteModal}
            onClose={handleShowDeleteModal}
            onDelete={onDelete}
          />
        </Fragment>
      )}
    </DrawerCM>
  );
}
